import type { ReactElement } from 'react';
import { Alert, Box, Button, FormGroup as NewFormGroup } from '@ksyos/design-system';
import type { AfwijkingOption } from '@common/customControls/tooV1Afwijking';
import type {
    IAOAfwijkingen,
    IVOAfwijkingen,
} from '@common/interfaces/ophthalmology/TOO/tooDiagnosticReportCustomControlData';
import type {
    ITooDiagnosticReportCustomControlDataOptometrischeDiagnose,
    TooOphthalmologyPolicies,
    UrgenciesForConsultOogartsNoAnders,
    UrgenciesForRevisionOptometrist,
} from '@common/validation/customControls/ophthalmology';
import type { ValidationErrors } from '@frontend/utils/validation';
import {
    afwijkingInAoOptions,
    afwijkingInVoOptions,
    afwijkingWriteOptions,
    isAfwijkingOption,
} from '@common/customControls/tooV1Afwijking';
import { tooV1DiagnosticReportInitializeCustomControlData } from '@common/customControls/tooV1DiagnosticReportInitializeCustomControlData';
import { ConclusieDiagnoseOption } from '@common/enums/conclusieDiagnoseOption';
import {
    OphthalmologyPoliciesEnum,
    TooOphthalmologyPoliciesEnum,
    TooV1DiagnosticReportCustomControlDataConclusieOption,
    UrgenciesForConsultOogartsEnum,
    UrgenciesForConsultOogartsNoAndersEnum,
} from '@common/validation/customControls/ophthalmology';
import { nullDiagnosis } from '@common/validation/diagnosis';
import { InputPeriod } from '@frontend/components/elements/InputPeriod';
import { SearchableEnumDropdown } from '@frontend/components/elements/SearchableEnumDropdown';
import { translateKey } from '@frontend/translate';
import { getErrorStartingWithKey } from '@frontend/utils/validation';
import type { IServiceGetOne } from '../../../../interfaces/serviceGetOne';
import type { IDiagnosticReportAddEdit } from '../../../diagnosticReports/DiagnosticReportItemAddEdit';
import { CheckMarkButton } from '../../../../elements/buttons/checkMarkButton/CheckMarkButton';
import {
    ButtonContainer,
    FormDivider,
    HeaderDivider,
    InputCheckbox,
    InputRadioGroup,
    InputTextArea,
} from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { Figure } from '../../../../elements/figure/Figure';
import { FormContextProvider } from '../../../../elements/formContext';
import { FormGroup } from '../../../../elements/forms/FormGroup';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { InputControl } from '../../../../elements/forms/inputControl/InputControl';
import { Modal } from '../../../../elements/modals/Modal';
import { TextAreaField } from '../../../../fields/TextArea';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { Icd10Selections } from '../../utils/v1/Icd10/Icd10Selections';
import { TooV1CustomControlFields } from './TooV1DiagnosticReportAddEdit';

export interface ICustomControlData {
    afwijking_in_ao: IAOAfwijkingen;
    afwijking_in_vo: IVOAfwijkingen;
    conclusie_ao: string | null;
    conclusie_diagnose: ConclusieDiagnoseOption | null;
    conclusie_vo: string | null;
    geadviseerd_beleid: TooOphthalmologyPolicies | null;
    geadviseerd_beleid_anders: string | null;
    optometrische_diagnose: ITooDiagnosticReportCustomControlDataOptometrischeDiagnose;
    toelichting: string | null;
    urgentie: UrgenciesForRevisionOptometrist | UrgenciesForConsultOogartsNoAnders | null;
}

interface ITooV1DiagnosticReportAddEditCustomControlFieldsProps {
    customControlData: ICustomControlData;
    diagnosticReport: IDiagnosticReportAddEdit;
    errors: ValidationErrors;
    handleChange: <K extends keyof IDiagnosticReportAddEdit>(
        name: K,
        value: IDiagnosticReportAddEdit[K]
    ) => Promise<void>;
    handleChangeCustomControl: (values: Partial<ICustomControlData>) => Promise<void>;
    service: IServiceGetOne;
    showGlaucoomAlertMessage?: boolean;
    validateOne: (name: keyof IDiagnosticReportAddEdit) => Promise<void>;
    validateOneCustomControl: (name: string) => Promise<void>;
}

interface ITooV1DiagnosticReportAddEditState {
    afwijkingEdit:
        | ICustomControlData[TooV1CustomControlFields.afwijking_in_vo | TooV1CustomControlFields.afwijking_in_ao]
        | null;
    afwijkingEditKey: TooV1CustomControlFields.afwijking_in_vo | TooV1CustomControlFields.afwijking_in_ao | null;
    andersCheck: boolean;
    modalName: AfwijkingOption | null;
    previousAfwijkingEdit:
        | ICustomControlData[TooV1CustomControlFields.afwijking_in_vo | TooV1CustomControlFields.afwijking_in_ao]
        | null;
    showModal: TooV1CustomControlFields.afwijking_in_vo | TooV1CustomControlFields.afwijking_in_ao | null;
}

export const urgenciesForConsultOogarts = [
    UrgenciesForConsultOogartsEnum.a1_spoed_binnen_24_uur,
    UrgenciesForConsultOogartsEnum.a2_spoed_binnen_2_tot_4_werkdagen,
    UrgenciesForConsultOogartsEnum.b_voorrangsverwijzing,
    UrgenciesForConsultOogartsEnum.c1_consult_gewone_verwijzing,
    UrgenciesForConsultOogartsEnum.c2_consult_gewone_verwijzing,
    UrgenciesForConsultOogartsEnum.anders,
];

export class TooV1DiagnosticReportAddEditCustomControlFields extends Component<
    ITooV1DiagnosticReportAddEditCustomControlFieldsProps,
    ITooV1DiagnosticReportAddEditState
> {
    public constructor(props: ITooV1DiagnosticReportAddEditCustomControlFieldsProps) {
        super(props);

        this.state = {
            afwijkingEdit: null,
            afwijkingEditKey: null,
            andersCheck:
                this.props.diagnosticReport.diagnosis.diagnosisConsiderationIcd10 !== null ||
                this.props.diagnosticReport.diagnosis.considerationThesaurusTermId !== null,
            modalName: null,
            previousAfwijkingEdit: null,
            showModal: null,
        };
    }

    public render() {
        return (
            <>
                <HeaderDivider text={translateKey('diagnosticReport.findingsFrontEyeSegment')} />
                <HorizontalFormGroup
                    className="layout center"
                    hasErrors={this.props.errors.has('customControlData.conclusie_vo')}
                >
                    <FormLabel isRequired>Oogleden, Conjunctiva, Cornea, VOK, Iris, Lens</FormLabel>
                    <div className="col-sm-auto">
                        <InputRadioGroup
                            automation="conclusieVo"
                            error={this.props.errors.get('customControlData.conclusie_vo')}
                            name="conclusie_vo"
                            onChange={async (value) =>
                                await this.handleChangeConclusie(
                                    'conclusie_vo',
                                    value as TooV1DiagnosticReportCustomControlDataConclusieOption
                                )
                            }
                            onDoValidate={async () => await this.props.validateOneCustomControl('conclusie_vo')}
                            options={[
                                TooV1DiagnosticReportCustomControlDataConclusieOption.normal,
                                TooV1DiagnosticReportCustomControlDataConclusieOption.different,
                            ]}
                            value={this.props.customControlData.conclusie_vo}
                        />
                    </div>

                    <div className="col-sm-auto offset-sm-3">
                        <Figure
                            alt="Voorste oogsegment"
                            fixedSquareSizes="is96X96"
                            imageUrl="../../../../../assets/images/voorste_oogsegment.svg"
                        />
                    </div>
                </HorizontalFormGroup>

                {this.props.customControlData.conclusie_vo ===
                    TooV1DiagnosticReportCustomControlDataConclusieOption.different &&
                    this.afwijkingInOptions(TooV1CustomControlFields.afwijking_in_vo)}

                <FormDivider />

                <HeaderDivider text={translateKey('diagnosticReport.findingsRearEyeSegment')} />
                <HorizontalFormGroup
                    className="layout center"
                    hasErrors={this.props.errors.has('customControlData.conclusie_ao')}
                >
                    <FormLabel isRequired>Glasvocht, Fundus, Papil, Macula </FormLabel>
                    <div className="col-sm-auto">
                        <InputRadioGroup
                            automation="conclusieAo"
                            error={this.props.errors.get('customControlData.conclusie_ao')}
                            name="conclusie_ao"
                            onChange={async (value) =>
                                await this.handleChangeConclusie(
                                    'conclusie_ao',
                                    value as TooV1DiagnosticReportCustomControlDataConclusieOption
                                )
                            }
                            onDoValidate={async () => await this.props.validateOneCustomControl('conclusie_ao')}
                            options={[
                                TooV1DiagnosticReportCustomControlDataConclusieOption.normal,
                                TooV1DiagnosticReportCustomControlDataConclusieOption.different,
                            ]}
                            value={this.props.customControlData.conclusie_ao}
                        />
                    </div>

                    <div className="col-sm-auto offset-sm-3">
                        <Figure
                            alt="Achterste oogsegment"
                            fixedSquareSizes="is96X96"
                            imageUrl="../../../../../assets/images/achterste_oogsegment.svg"
                        />
                    </div>
                </HorizontalFormGroup>

                {this.props.customControlData.conclusie_ao ===
                    TooV1DiagnosticReportCustomControlDataConclusieOption.different &&
                    this.afwijkingInOptions(TooV1CustomControlFields.afwijking_in_ao)}

                <FormDivider />

                {this.optometrischeDiagnose()}

                <FormDivider />

                <HeaderDivider text="Advies" />

                <FormContextProvider
                    value={{
                        errors: this.props.errors,
                        item: this.props.customControlData,
                        onChange: async (key, value) => await this.props.handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await this.props.validateOneCustomControl(key),
                        placeholderGroupName: 'diagnosticReport',
                    }}
                >
                    <NewFormGroup
                        hasErrors={this.props.errors.has('customControlData.geadviseerd_beleid')}
                        isRequired={true}
                        label="Geadviseerd beleid"
                    >
                        <SearchableEnumDropdown
                            automation="geadviseerdBeleid"
                            error={this.props.errors.get('customControlData.geadviseerd_beleid')}
                            jsEnum={TooOphthalmologyPoliciesEnum}
                            onChange={async (value) => {
                                const update = { ...this.props.customControlData, geadviseerd_beleid: value };
                                if (
                                    value === TooOphthalmologyPoliciesEnum.geenVervolgactie ||
                                    value === TooOphthalmologyPoliciesEnum.terugverwijzingNaarHuisarts ||
                                    value === TooOphthalmologyPoliciesEnum.anders ||
                                    value === TooOphthalmologyPoliciesEnum.verwijzingOrthoptist
                                ) {
                                    update.urgentie = null;
                                }
                                if (update.geadviseerd_beleid_anders && value !== TooOphthalmologyPoliciesEnum.anders) {
                                    update.geadviseerd_beleid_anders = null;
                                }
                                await this.props.handleChangeCustomControl(update);
                            }}
                            onDoValidate={async () => {
                                await this.props.validateOneCustomControl('geadviseerd_beleid');
                            }}
                            placeholder="Kies uit de lijst"
                            value={this.props.customControlData.geadviseerd_beleid as TooOphthalmologyPolicies}
                        />
                    </NewFormGroup>

                    {this.props.customControlData.geadviseerd_beleid ===
                        TooOphthalmologyPoliciesEnum.revisieOptometrist && (
                        <NewFormGroup
                            hasErrors={this.props.errors.has('customControlData.urgentie')}
                            isRequired={true}
                            label="Urgentie"
                        >
                            <InputPeriod
                                {...this.props}
                                errors={this.props.errors.get('customControlData.urgentie')}
                                fieldKey="urgentie"
                                value={this.props.customControlData.urgentie}
                            />
                        </NewFormGroup>
                    )}

                    {(
                        [
                            OphthalmologyPoliciesEnum.consultOogartsDoorHuisarts,
                            OphthalmologyPoliciesEnum.consultOogartsDoorOptometrist,
                        ] as TooOphthalmologyPolicies[]
                    ).includes(this.props.customControlData.geadviseerd_beleid as TooOphthalmologyPolicies) && (
                        <NewFormGroup
                            hasErrors={this.props.errors.has('customControlData.urgentie')}
                            isRequired={true}
                            label="Urgentie"
                        >
                            <SearchableEnumDropdown
                                automation="urgentie"
                                error={this.props.errors.get('customControlData.urgentie')}
                                jsEnum={UrgenciesForConsultOogartsNoAndersEnum}
                                onChange={async (value) => {
                                    await this.props.handleChangeCustomControl({ urgentie: value });
                                }}
                                onDoValidate={async () => {
                                    await this.props.validateOneCustomControl('urgentie');
                                }}
                                placeholder="Kies uit de lijst"
                                value={this.props.customControlData.urgentie as UrgenciesForConsultOogartsNoAnders}
                            />
                        </NewFormGroup>
                    )}

                    {this.props.customControlData.geadviseerd_beleid === TooOphthalmologyPoliciesEnum.anders && (
                        <TextAreaField
                            error={this.props.errors.get('customControlData.geadviseerd_beleid_anders')}
                            fieldKey="geadviseerd_beleid_anders"
                            hasErrors={this.props.errors.has('customControlData.geadviseerd_beleid_anders')}
                            label=" "
                            placeholder="Toelichting beleid"
                            value={this.props.customControlData.geadviseerd_beleid_anders}
                        />
                    )}

                    {(this.props.customControlData.urgentie ===
                        UrgenciesForConsultOogartsNoAndersEnum.a1_spoed_binnen_24_uur ||
                        this.props.customControlData.urgentie ===
                            UrgenciesForConsultOogartsNoAndersEnum.a2_spoed_binnen_2_tot_4_werkdagen) && (
                        <Alert appearance="info">
                            Let op: handel de verwijzing direct af. Verwijs de patiënt zelf naar de oogarts of bel de
                            huisarts voor een verwijzing. Indien dit niet mogelijk is, informeer Ksyos (020-6000060).
                        </Alert>
                    )}

                    <TextAreaField
                        automation="toelichting"
                        error={this.props.errors.get('customControlData.toelichting')}
                        fieldKey="toelichting"
                        hasErrors={this.props.errors.has('customControlData.toelichting')}
                        label="Toelichting"
                        placeholder="Toelichting"
                        value={this.props.customControlData.toelichting}
                    />
                </FormContextProvider>
            </>
        );
    }

    private getOptionName(name: string) {
        if (name === 'perifere_retina') {
            return 'Fundus';
        } else {
            name = name.charAt(0).toUpperCase() + name.slice(1);

            return name.replace(/_/g, ' ');
        }
    }

    private createAfwijkingButtons(
        customControlDataKey: TooV1CustomControlFields.afwijking_in_vo | TooV1CustomControlFields.afwijking_in_ao
    ) {
        const customControlDataPart = this.props.customControlData[customControlDataKey];
        const afwijkingKeyOptions =
            customControlDataKey === TooV1CustomControlFields.afwijking_in_vo
                ? afwijkingInVoOptions
                : afwijkingInAoOptions;

        const afwijkingButtons: ReactElement[] = afwijkingKeyOptions.map((option) => {
            return (
                <CheckMarkButton
                    automation={customControlDataKey}
                    className="mb-2"
                    isChecked={
                        !getErrorStartingWithKey(
                            this.props.errors,
                            `customControlData.${customControlDataKey}.${option}`
                        ) && customControlDataPart[option as keyof typeof customControlDataPart]
                    }
                    key={`afwijking-${option}`}
                    onClick={() =>
                        this.setState({
                            afwijkingEdit: customControlDataPart,
                            afwijkingEditKey: customControlDataKey,
                            modalName: option,
                            previousAfwijkingEdit: getErrorStartingWithKey(
                                this.props.errors,
                                `customControlData.${customControlDataKey}.${option}`
                            )
                                ? this.state.previousAfwijkingEdit
                                : customControlDataPart,
                            showModal: customControlDataKey,
                        })
                    }
                >
                    {this.getOptionName(option)}
                </CheckMarkButton>
            );
        });

        return afwijkingButtons;
    }

    private afwijkingInOptions(
        customControlDataKey: TooV1CustomControlFields.afwijking_in_vo | TooV1CustomControlFields.afwijking_in_ao
    ) {
        return (
            <FormContextProvider
                value={{
                    errors: this.props.errors,
                    item: this.state.afwijkingEdit || {},
                    onChange: async (key, value) => {
                        await this.handleChangeAfwijking(customControlDataKey, key, value);
                    },
                    onDoValidate: async () => await this.props.validateOneCustomControl(customControlDataKey),
                    placeholderGroupName: 'diagnosticReport',
                }}
            >
                <HorizontalFormGroup hasErrors={this.props.errors.has(`customControlData.${customControlDataKey}`)}>
                    <div className="pl-0 col-sm-18 offset-sm-6">
                        <ButtonContainer
                            buttons={this.createAfwijkingButtons(customControlDataKey)}
                            noMargin
                            wrapContent
                        />
                    </div>
                    {this.state.showModal === customControlDataKey && this.showModal()}
                </HorizontalFormGroup>

                {this.props.errors.has(`customControlData.${customControlDataKey}`) && !this.state.showModal && (
                    <Alert appearance="warning">
                        Kies minstens één afwijking. Klik op de locatie van de afwijking om een selectie te maken.
                    </Alert>
                )}
            </FormContextProvider>
        );
    }

    private optometrischeDiagnose() {
        return (
            <FormContextProvider
                value={{
                    errors: this.props.errors,
                    item: this.props.customControlData.optometrische_diagnose,
                    onChange: async (key, value) => {
                        await this.handleChangeOptometrischeDiagnose(key, value);
                    },
                    onDoValidate: async () =>
                        await this.props.validateOneCustomControl(TooV1CustomControlFields.optometrische_diagnose),
                    placeholderGroupName: 'diagnosticReport',
                }}
            >
                <ProcessItemHeader
                    isFirstItem={false}
                    isItemOpen
                    process={undefined}
                    title={translateKey('common.conclusion')}
                />

                <HeaderDivider text={translateKey('diagnosticReport.optometricDiagnosis')} />

                <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.conclusie_diagnose')}>
                    <FormLabel isRequired>Heb je een diagnose?</FormLabel>
                    <InputContainer>
                        <InputRadioGroup
                            automation="conclusieDiagnose"
                            error={this.props.errors.get('customControlData.conclusie_diagnose')}
                            name="conclusie_diagnose"
                            onChange={async (value) =>
                                await this.handleChangeConclusieDiagnose(value as ConclusieDiagnoseOption)
                            }
                            onDoValidate={async () => await this.props.validateOneCustomControl('conclusie_diagnose')}
                            options={[
                                ConclusieDiagnoseOption.ja,
                                ConclusieDiagnoseOption.nog_geen_diagnose,
                                ConclusieDiagnoseOption.geen_afwijkingen,
                            ]}
                            value={this.props.customControlData.conclusie_diagnose}
                        />
                    </InputContainer>
                </HorizontalFormGroup>
                {this.props.customControlData.conclusie_diagnose === ConclusieDiagnoseOption.ja && (
                    <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.optometrische_diagnose')}>
                        <FormLabel isRequired>Optometrische diagnose</FormLabel>
                        <InputContainer>
                            <InputCheckbox
                                automation="cataract"
                                checked={this.props.customControlData.optometrische_diagnose.cataract}
                                fieldKey="cataract"
                                text="Cataract"
                            />
                            <InputCheckbox
                                checked={this.props.customControlData.optometrische_diagnose.macula_degeneratie}
                                fieldKey="macula_degeneratie"
                                onChange={async (value) => await this.handleChangeMaculaDegeneratie(value)}
                                text="Maculadegeneratie"
                            />
                            {this.props.customControlData.optometrische_diagnose.macula_degeneratie && (
                                <InputControl
                                    hasErrors={this.props.errors.has('customControlData.optometrische_diagnose')}
                                    indented
                                >
                                    <InputCheckbox
                                        checked={
                                            this.props.customControlData.optometrische_diagnose.macula_degeneratie_nat
                                        }
                                        fieldKey="macula_degeneratie_nat"
                                        text="Nat"
                                    />
                                    <InputCheckbox
                                        checked={
                                            this.props.customControlData.optometrische_diagnose.macula_degeneratie_droog
                                        }
                                        fieldKey="macula_degeneratie_droog"
                                        text="Droog"
                                    />
                                    <InputCheckbox
                                        checked={
                                            this.props.customControlData.optometrische_diagnose
                                                .macula_degeneratie_suspect
                                        }
                                        fieldKey="macula_degeneratie_suspect"
                                        text="Suspect"
                                    />
                                </InputControl>
                            )}
                            <InputCheckbox
                                automation="retinopathie"
                                checked={this.props.customControlData.optometrische_diagnose.retinopathie}
                                fieldKey="retinopathie"
                                onChange={async (value) => await this.handleChangeRetinopathie(value)}
                                text="Retinopathie"
                            />
                            {this.props.customControlData.optometrische_diagnose.retinopathie && (
                                <InputControl
                                    hasErrors={this.props.errors.has('customControlData.optometrische_diagnose')}
                                    indented
                                >
                                    <InputCheckbox
                                        checked={
                                            this.props.customControlData.optometrische_diagnose.retinopathie_diabetische
                                        }
                                        fieldKey="retinopathie_diabetische"
                                        text="Diabetische retinopathie"
                                    />
                                    <InputCheckbox
                                        checked={
                                            this.props.customControlData.optometrische_diagnose.retinopathie_overig
                                        }
                                        fieldKey="retinopathie_overig"
                                        text="Retinopathie overig"
                                    />
                                </InputControl>
                            )}
                            <InputCheckbox
                                checked={this.props.customControlData.optometrische_diagnose.glaucoom}
                                fieldKey="glaucoom"
                                onChange={async (value) => await this.handleChangeGlaucoom(value)}
                                text="Glaucoom"
                            />
                            {this.props.customControlData.optometrische_diagnose.glaucoom && (
                                <InputControl
                                    hasErrors={this.props.errors.has('customControlData.optometrische_diagnose')}
                                    indented
                                >
                                    <InputCheckbox
                                        checked={this.props.customControlData.optometrische_diagnose.glaucoom_ja}
                                        fieldKey="glaucoom_ja"
                                        text="Ja"
                                    />
                                    <InputCheckbox
                                        checked={this.props.customControlData.optometrische_diagnose.glaucoom_nee}
                                        fieldKey="glaucoom_nee"
                                        text="Nee"
                                    />
                                    <InputCheckbox
                                        checked={this.props.customControlData.optometrische_diagnose.glaucoom_suspect}
                                        fieldKey="glaucoom_suspect"
                                        text="Suspect"
                                    />
                                </InputControl>
                            )}

                            <InputCheckbox
                                automation="droge_ogen"
                                checked={this.props.customControlData.optometrische_diagnose.droge_ogen}
                                fieldKey="droge_ogen"
                                text="Droge ogen"
                            />

                            <InputControl>
                                <FormGroup isGrouped>
                                    <InputCheckbox
                                        checked={this.state.andersCheck}
                                        checkOnTextClickedDisabled
                                        onChange={async (value) => {
                                            this.setState({ andersCheck: value });
                                            if (!value) {
                                                await this.clearDiagnosis();
                                            }
                                        }}
                                        onDoValidate={async () => {
                                            await this.props.validateOne('diagnosis');
                                            await this.props.validateOneCustomControl(
                                                TooV1CustomControlFields.optometrische_diagnose
                                            );
                                        }}
                                    />
                                    <InputContainer>
                                        <Icd10Selections
                                            bold
                                            diagnosis={this.props.diagnosticReport.diagnosis}
                                            errors={this.props.errors}
                                            hideWorkingDiagnosisLabel
                                            isWorkingDiagnosis={false}
                                            onChange={async (diagnosis) => {
                                                await this.props.handleChange('diagnosis', diagnosis);
                                                await this.setState({
                                                    andersCheck:
                                                        diagnosis.diagnosisConsiderationIcd10 !== null ||
                                                        diagnosis.considerationThesaurusTermId !== null,
                                                });
                                            }}
                                            onDoValidate={async () => {
                                                await this.props.validateOne('diagnosis');
                                                await this.props.validateOneCustomControl(
                                                    TooV1CustomControlFields.optometrische_diagnose
                                                );
                                            }}
                                            specialismCode={this.props.service.specialismCode || ''}
                                        />
                                    </InputContainer>
                                </FormGroup>
                            </InputControl>
                        </InputContainer>
                    </HorizontalFormGroup>
                )}
                {this.showAlertMessage() && (
                    <HorizontalFormGroup>
                        <InputContainer>
                            <Alert appearance="warning">{this.createOptometrischeDiagnoseAlertMessage()}</Alert>
                        </InputContainer>
                    </HorizontalFormGroup>
                )}
            </FormContextProvider>
        );
    }

    private showAlertMessage() {
        const optometrische_diagnose = this.props.customControlData.optometrische_diagnose;

        return (
            optometrische_diagnose.cataract ||
            optometrische_diagnose.macula_degeneratie ||
            optometrische_diagnose.glaucoom
        );
    }

    private createOptometrischeDiagnoseAlertMessage() {
        const optometrische_diagnose = this.props.customControlData.optometrische_diagnose;
        const diagnosisLabels = [];
        const examinationLabels = [];

        if (optometrische_diagnose.cataract) {
            diagnosisLabels.push('cataract');
            examinationLabels.push('nabij visus');
        }

        if (optometrische_diagnose.macula_degeneratie) {
            diagnosisLabels.push('macula degeneratie');
            examinationLabels.push('amsler test');
        }

        if (optometrische_diagnose.glaucoom) {
            diagnosisLabels.push('glaucoom');
            examinationLabels.push('pachymetrie', 'gonioscopie');
        }

        let diagnosisPartMessage;
        let examinationPartMessage;

        if (diagnosisLabels.length > 2) {
            const lastDiagnosisLabel = diagnosisLabels.pop();
            const lastExaminationlabel = examinationLabels.pop();
            diagnosisPartMessage = diagnosisLabels.join(', ') + ' en ' + lastDiagnosisLabel;
            examinationPartMessage = examinationLabels.join(', ') + ' en ' + lastExaminationlabel;
        } else {
            diagnosisPartMessage = diagnosisLabels.join(' en ');
            examinationPartMessage = examinationLabels.join(' en ');
        }

        const baseMessage = `Vul bij de ${diagnosisLabels.length > 1 ? 'diagnoses' : 'diagnose'} `;
        const octFotoMessage = optometrische_diagnose.glaucoom ? 'en voeg een OCT foto toe' : '';

        return [baseMessage, diagnosisPartMessage, 'ook', examinationPartMessage, 'in', octFotoMessage].join(' ');
    }

    private async clearDiagnosis() {
        await this.props.handleChange('diagnosis', { ...nullDiagnosis });
    }

    private afwijkingOptions(
        customControlDataKey: TooV1CustomControlFields.afwijking_in_vo | TooV1CustomControlFields.afwijking_in_ao,
        name: AfwijkingOption
    ) {
        const content: ReactElement[] = [];
        const dividedContent: ReactElement[] = [];
        let rows: ReactElement[] = [];
        const elementsPerColumn: number = name === 'perifere_retina' ? 8 : 5;
        const afwijkingEdit = this.state.afwijkingEdit as any;

        afwijkingWriteOptions[name].map((option) => {
            if (option.key !== 'andersText') {
                const odKey = `${name}Od_${option.key}`;
                const osKey = `${name}Os_${option.key}`;
                content.push(
                    <Box
                        display="flex"
                        key={option.key}
                    >
                        <InputCheckbox
                            checked={!!afwijkingEdit?.[odKey]}
                            fieldKey={odKey}
                            onChange={async (value) => {
                                await this.setState({
                                    afwijkingEdit: {
                                        ...afwijkingEdit,
                                        [odKey]: value,
                                    },
                                });
                                await this.handleParentOption(name);
                            }}
                        />
                        <InputCheckbox
                            checked={!!afwijkingEdit?.[osKey]}
                            fieldKey={osKey}
                            onChange={async (value) => {
                                await this.setState({
                                    afwijkingEdit: {
                                        ...afwijkingEdit,
                                        [osKey]: value,
                                    },
                                });
                                await this.handleParentOption(name);
                            }}
                            text={option.text}
                        />
                    </Box>
                );
            } else {
                const odKey = `${name}Od_andersCheck`;
                const osKey = `${name}Os_andersCheck`;
                const textKey = `${name}AndersText`;

                content.push(
                    <Box
                        display="flex"
                        key={option.key}
                    >
                        <InputCheckbox
                            checked={!!afwijkingEdit?.[odKey]}
                            onChange={async (value) => {
                                const updatedAfwijking = { ...afwijkingEdit, [odKey]: value };
                                if (!value && !afwijkingEdit?.[osKey]) {
                                    updatedAfwijking[textKey] = null;
                                }
                                await this.setState({ afwijkingEdit: updatedAfwijking });
                                await this.handleParentOption(name);
                            }}
                        />
                        <InputCheckbox
                            checked={!!afwijkingEdit?.[osKey]}
                            onChange={async (value) => {
                                const updatedAfwijking = { ...afwijkingEdit, [osKey]: value };
                                if (!value && !afwijkingEdit?.[odKey]) {
                                    updatedAfwijking[textKey] = null;
                                }
                                await this.setState({ afwijkingEdit: updatedAfwijking });
                                await this.handleParentOption(name);
                            }}
                        />
                        <InputControl inline>
                            <InputTextArea
                                automation={textKey}
                                disabled={!afwijkingEdit?.[odKey] && !afwijkingEdit?.[osKey]}
                                fieldKey={textKey}
                                placeholder="Anders, namelijk..."
                                value={afwijkingEdit?.[textKey]}
                            />
                        </InputControl>
                    </Box>
                );
            }
        });
        for (let column = 0; column < afwijkingWriteOptions[name].length / elementsPerColumn; column++) {
            rows = [];
            for (let row = 0; row < elementsPerColumn; row++) {
                rows.push(content[column * elementsPerColumn + row]);
            }
            dividedContent.push(
                <div
                    className="col-sm-auto col-lg-8"
                    key={column}
                >
                    <Box display="flex">
                        <InputControl
                            className="mr-1"
                            inline
                            isFormControl
                        >
                            OD
                        </InputControl>
                        <InputControl isFormControl>OS</InputControl>
                    </Box>
                    {rows}
                </div>
            );
        }

        return (
            <HorizontalFormGroup hasErrors={this.props.errors.has(`customControlData.${customControlDataKey}.${name}`)}>
                {dividedContent}
            </HorizontalFormGroup>
        );
    }

    private resetAfwijkingOptions(afwijking: any, name: AfwijkingOption) {
        const options = afwijkingWriteOptions[name];
        afwijking[name] = false;

        for (const option of options) {
            afwijking[`${name}Od_${option.key}`] = false;
            afwijking[`${name}Os_${option.key}`] = false;
        }

        afwijking[`${name}Od_andersCheck`] = false;
        afwijking[`${name}Os_andersCheck`] = false;
        afwijking[`${name}AndersText`] = null;
    }

    private async handleParentOption(name: AfwijkingOption) {
        const superOption = Object.entries(this.state.afwijkingEdit || {}).some(
            ([key, value]) => value && key.includes(name) && key !== name
        );
        await this.setState(({ afwijkingEdit }) => ({
            afwijkingEdit: {
                ...afwijkingEdit,
                [name]: superOption,
            },
        }));
    }

    private async handleChangeAfwijking(
        customControlPart: TooV1CustomControlFields.afwijking_in_vo | TooV1CustomControlFields.afwijking_in_ao,
        name: string,
        value: boolean | string | null
    ) {
        const afwijking = this.props.customControlData[customControlPart];
        if (isAfwijkingOption(name)) {
            this.resetAfwijkingOptions(afwijking, name);
        }

        await this.setState(({ afwijkingEdit }) => ({
            afwijkingEdit: {
                ...afwijkingEdit,
                [name]: value,
            },
        }));
    }

    private async handleChangeOptometrischeDiagnose<
        K extends keyof ITooDiagnosticReportCustomControlDataOptometrischeDiagnose,
    >(name: K, value: ITooDiagnosticReportCustomControlDataOptometrischeDiagnose[K]) {
        await this.props.handleChangeCustomControl({
            optometrische_diagnose: {
                ...this.props.customControlData.optometrische_diagnose,
                [name]: value,
            },
        });
    }

    private async handleChangeConclusie(
        field: 'conclusie_vo' | 'conclusie_ao',
        value: TooV1DiagnosticReportCustomControlDataConclusieOption
    ) {
        if (value === TooV1DiagnosticReportCustomControlDataConclusieOption.normal) {
            let afwijking;

            if (field === 'conclusie_vo') {
                afwijking = this.props.customControlData[TooV1CustomControlFields.afwijking_in_vo];
                this.resetAfwijkingOptions(afwijking, 'oogleden');
                this.resetAfwijkingOptions(afwijking, 'conjunctiva');
                this.resetAfwijkingOptions(afwijking, 'cornea');
                this.resetAfwijkingOptions(afwijking, 'vok');
                this.resetAfwijkingOptions(afwijking, 'iris');
                this.resetAfwijkingOptions(afwijking, 'lens');
                await this.props.handleChangeCustomControl({ [TooV1CustomControlFields.afwijking_in_vo]: afwijking });
            } else if (field === 'conclusie_ao') {
                afwijking = this.props.customControlData[TooV1CustomControlFields.afwijking_in_ao];
                this.resetAfwijkingOptions(afwijking, 'glasvocht');
                this.resetAfwijkingOptions(afwijking, 'perifere_retina');
                this.resetAfwijkingOptions(afwijking, 'papil');
                this.resetAfwijkingOptions(afwijking, 'macula');
                await this.props.handleChangeCustomControl({ [TooV1CustomControlFields.afwijking_in_ao]: afwijking });
            }
        }
        await this.props.handleChangeCustomControl({ [field]: value });
    }

    private showModal() {
        const { afwijkingEdit, afwijkingEditKey, modalName, showModal } = this.state;
        const afwijkingError = getErrorStartingWithKey(
            this.props.errors,
            `customControlData.${afwijkingEditKey}.${modalName}`
        );

        return (
            modalName &&
            afwijkingEditKey &&
            afwijkingEdit && (
                <Modal
                    actionButtons={[
                        <Button
                            key={`{modalName}-cancel`}
                            onClick={async () => await this.cancelModal(afwijkingEditKey)}
                            variant="outline"
                        >
                            {translateKey('common.cancel')}
                        </Button>,
                        <Button
                            className={'automation-' + modalName + '-submit'}
                            key={`modalName-submit`}
                            onClick={async () => {
                                await this.props.handleChangeCustomControl({ [afwijkingEditKey]: afwijkingEdit });
                                await this.props.validateOneCustomControl(afwijkingEditKey);
                                if (
                                    !getErrorStartingWithKey(
                                        this.props.errors,
                                        `customControlData.${afwijkingEditKey}.${modalName}`
                                    )
                                ) {
                                    await this.setState({ showModal: null });
                                }
                            }}
                        >
                            {translateKey('common.saveAndProceed')}
                        </Button>,
                    ]}
                    automation={modalName + '-modal'}
                    isOpen={!!showModal}
                    onClose={async () => await this.cancelModal(afwijkingEditKey)}
                    size="large"
                    title={`Afwijking ${this.getOptionName(modalName)}`}
                >
                    {this.afwijkingOptions(afwijkingEditKey, modalName)}
                    {afwijkingError && (
                        <Alert appearance="warning">
                            {afwijkingError === 'Mag niet leeg zijn'
                                ? 'Geef hierbij altijd een toelichting.'
                                : afwijkingError}
                        </Alert>
                    )}
                </Modal>
            )
        );
    }

    private async cancelModal(
        afwijkingEditKey: TooV1CustomControlFields.afwijking_in_vo | TooV1CustomControlFields.afwijking_in_ao
    ) {
        await this.props.handleChangeCustomControl({ [afwijkingEditKey]: this.state.previousAfwijkingEdit });
        await this.props.validateOneCustomControl(afwijkingEditKey);
        await this.setState({ showModal: null });
    }

    private async handleChangeConclusieDiagnose(value: ConclusieDiagnoseOption) {
        await this.props.handleChangeCustomControl({
            optometrische_diagnose: { ...tooV1DiagnosticReportInitializeCustomControlData().optometrische_diagnose },
        });
        await this.setState({ andersCheck: false });
        await this.clearDiagnosis();
        await this.props.handleChangeCustomControl({ conclusie_diagnose: value });
    }

    private async handleChangeMaculaDegeneratie(value: boolean) {
        const update = { ...this.props.customControlData.optometrische_diagnose };
        update.macula_degeneratie = value;

        if (!value) {
            update.macula_degeneratie_nat = false;
            update.macula_degeneratie_droog = false;
            update.macula_degeneratie_suspect = false;
        }

        await this.props.handleChangeCustomControl({ optometrische_diagnose: update });
    }

    private async handleChangeRetinopathie(value: boolean) {
        const update = { ...this.props.customControlData.optometrische_diagnose };
        update.retinopathie = value;

        if (!value) {
            update.retinopathie_diabetische = false;
            update.retinopathie_overig = false;
        }

        await this.props.handleChangeCustomControl({ optometrische_diagnose: update });
    }

    private async handleChangeGlaucoom(value: boolean) {
        const update = { ...this.props.customControlData.optometrische_diagnose };
        update.glaucoom = value;

        if (!value) {
            update.glaucoom_ja = false;
            update.glaucoom_nee = false;
            update.glaucoom_suspect = false;
        }

        await this.props.handleChangeCustomControl({ optometrische_diagnose: update });
    }
}
